import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, FileText, CaretDown, CaretUp, Stack } from '@phosphor-icons/react';
import { toast } from 'sonner';
import QuoteDetails, { QuoteStatusBadge, eur, fmtDate } from '../components/QuoteDetails';
import QuoteFormDialog from '../components/QuoteFormDialog';
import QuoteTemplatesDialog from '../components/QuoteTemplatesDialog';

const FILTERS = [['all', 'Tutti'], ['sent', 'Inviati'], ['accepted', 'Accettati'], ['rejected', 'Rifiutati'], ['expired', 'Scaduti'], ['cancelled', 'Annullati']];

const QuoteRow = ({ q, onCancel }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-4" data-testid={`quote-card-${q.quote_id}`}>
      <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`quote-toggle-${q.quote_id}`}>
        <FileText size={22} className="text-primary shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">N. {q.number} · {q.client_name}</div>
          <div className="text-xs text-slate-500 truncate">{q.service} · inviato il {fmtDate(q.created_at)} · valido fino al {fmtDate(q.valid_until)}</div>
        </div>
        <div className="font-heading font-semibold">{eur(q.total)}</div>
        <QuoteStatusBadge status={q.status} id={q.quote_id} />
        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
      </button>
      {open && (
        <div className="mt-4 pt-4 border-t space-y-3">
          <QuoteDetails quote={q} />
          {q.status === 'sent' && (
            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => onCancel(q)} data-testid={`quote-cancel-${q.quote_id}`}>Annulla preventivo</Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default function CenterQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await api.get('/api/quotes'); setQuotes(r.data.items || []); }
    catch { toast.error('Errore nel caricamento dei preventivi'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (q) => {
    if (!window.confirm(`Annullare il preventivo n. ${q.number}?`)) return;
    try { await api.post(`/api/quotes/${q.quote_id}/cancel`); toast.success('Preventivo annullato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Operazione non riuscita'); }
  };

  const shown = filter === 'all' ? quotes : quotes.filter((q) => q.status === filter);
  const count = (s) => quotes.filter((q) => q.status === s).length;

  return (
    <div className="p-4 md:p-8 max-w-5xl space-y-6" data-testid="center-quotes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Preventivi</h2>
          <p className="text-sm text-slate-500">Prepara e invia i preventivi ai clienti delle richieste che hai accettato.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setTemplatesOpen(true)} className="rounded-full" data-testid="quote-templates-btn"><Stack size={18} className="mr-1" /> Modelli</Button>
          <Button onClick={() => setFormOpen(true)} className="rounded-full" data-testid="new-quote-btn"><Plus size={18} className="mr-1" /> Nuovo preventivo</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`quote-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${filter === k ? 'bg-primary text-white' : 'bg-white border text-slate-600 hover:border-primary'}`}>
            {label}{k !== 'all' && ` (${count(k)})`}
          </button>
        ))}
      </div>
      {loading ? <p className="text-sm text-slate-500">Caricamento…</p> : shown.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="quotes-empty">Nessun preventivo{filter !== 'all' ? ' in questo stato' : ': crea il primo con «Nuovo preventivo»'}.</Card>
      ) : (
        <div className="space-y-3">{shown.map((q) => <QuoteRow key={q.quote_id} q={q} onCancel={cancel} />)}</div>
      )}
      <QuoteFormDialog open={formOpen} onOpenChange={setFormOpen} onCreated={load} />
      <QuoteTemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} />
    </div>
  );
}
